import { enviroment } from "../../enviroment";
import { fitSizeToTransferMethod } from "./SizeUtil";

// Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED (paired with the API's
// LogoMethodMaxSizeEnabled): the API owns the max logo size per transfer
// method and SCALES a logo above it DOWN - it never refuses one. An answer
// of the request in which the server did that carries
//   maxSizeClamp: { flow, fromWidthMm, fromHeightMm, widthMm, heightMm,
//                   maxSize, method }
// (upload, resize, method change, clone, approval start). The field is left
// out of every other answer and is never stored - a later GET of the logo
// does not have it. The answer's own widthMm / heightMm are always the
// final size; the marker only says THAT the server scaled and from what.
//
// The pure reading of that marker lives here, out of the toasts and the
// action handlers, so it can be unit-tested on its own. Flag off = every
// helper says "no marker": byte-identical behaviour.

export type MaxSizeClampFlow =
  | "upload"
  | "resize"
  | "method"
  | "clone"
  | "approve";

export interface LogoMaxSizeClamp {
  flow: MaxSizeClampFlow | string;
  /** The size before the server scaled it down. */
  fromWidthMm: number;
  fromHeightMm: number;
  /** The size the logo has now (= the answer's widthMm / heightMm). */
  widthMm: number;
  heightMm: number;
  /** The limit as the admin wrote it, "AxB" in whole mm. */
  maxSize: string;
  /** The method's display name (else its key). */
  method: string;
}

// i18n key of the sentence appended to the flow's own success text (all 10
// locale files), and how long that one toast stays: it tells the user the
// size changed, so it gets a little longer than the default 5 s.
export const MAX_SIZE_CLAMP_TOAST_KEY = "logoList.maxSizeClamped";
export const MAX_SIZE_CLAMP_TOAST_MS = 10_000;

function isWholeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * The marker of an answer in which the server scaled the logo down, or null:
 * flag off, no answer, no marker, or a marker without the sizes. Accepts the
 * mapped logo and the raw DTO alike (the mapper spreads the field through).
 */
export function readMaxSizeClamp(answer: unknown): LogoMaxSizeClamp | null {
  if (!enviroment.logoMethodMaxSizeEnabled) return null;
  if (answer === null || typeof answer !== "object") return null;
  const marker = (answer as { maxSizeClamp?: unknown }).maxSizeClamp;
  if (marker === null || typeof marker !== "object") return null;
  const raw = marker as Partial<LogoMaxSizeClamp>;
  if (
    !isWholeNumber(raw.fromWidthMm) ||
    !isWholeNumber(raw.fromHeightMm) ||
    !isWholeNumber(raw.widthMm) ||
    !isWholeNumber(raw.heightMm)
  ) {
    return null;
  }
  return {
    flow: typeof raw.flow === "string" ? raw.flow : "",
    fromWidthMm: raw.fromWidthMm,
    fromHeightMm: raw.fromHeightMm,
    widthMm: raw.widthMm,
    heightMm: raw.heightMm,
    maxSize: typeof raw.maxSize === "string" ? raw.maxSize : "",
    method: typeof raw.method === "string" ? raw.method : "",
  };
}

/** True when the server scaled the logo down in the request this answers. */
export function wasClampedByServer(answer: unknown): boolean {
  return readMaxSizeClamp(answer) !== null;
}

function isPositiveWholeNumber(value: unknown): value is number {
  return isWholeNumber(value) && value > 0 && Number.isInteger(value);
}

/**
 * The approval start after a 409 productPending retry: the API scaled the
 * draft down in the FIRST request (the marker sat on that 409's request),
 * waited <= 3 s for the worker's price and answered 409; the ONE automatic
 * retry (withProductPendingRetry) meets a logo that already fits, so its
 * answer carries no maxSizeClamp - and the toast would stay silent although
 * the size changed. This rebuilds the marker for exactly that case, and only
 * when the numbers prove it: the size the caller sent does NOT fit the
 * answer's limit (`transferMethod.size`, the admin value), and the answer's
 * size is PRECISELY what the policy makes of it (fitSizeToTransferMethod =
 * the API's Clamp). Anything else - the flag off, a marker already in the
 * answer (the server's own word wins), no limit, a size that fits, a 0 mm
 * side (an upload still pending), a different result - is null: no guess.
 * Only meaningful for an answer of a RETRY; the caller decides that.
 */
export function inferApprovalClampAfterRetry(
  sent: { widthMm?: number; heightMm?: number } | null | undefined,
  answer: unknown
): LogoMaxSizeClamp | null {
  if (!enviroment.logoMethodMaxSizeEnabled) return null;
  if (readMaxSizeClamp(answer) !== null) return null;
  if (!sent || answer === null || typeof answer !== "object") return null;
  const raw = answer as {
    widthMm?: unknown;
    heightMm?: unknown;
    transferMethod?: {
      size?: unknown;
      displayName?: unknown;
      key?: unknown;
    } | null;
  };
  const method = raw.transferMethod;
  const maxSize = method && typeof method.size === "string" ? method.size : "";
  if (
    !maxSize ||
    !isPositiveWholeNumber(sent.widthMm) ||
    !isPositiveWholeNumber(sent.heightMm) ||
    !isPositiveWholeNumber(raw.widthMm) ||
    !isPositiveWholeNumber(raw.heightMm)
  ) {
    return null;
  }
  const fit = fitSizeToTransferMethod(sent.widthMm, sent.heightMm, maxSize);
  const unchanged =
    fit.width === sent.widthMm && fit.height === sent.heightMm;
  if (unchanged || fit.width !== raw.widthMm || fit.height !== raw.heightMm) {
    return null;
  }
  const methodName =
    typeof method?.displayName === "string" && method.displayName
      ? method.displayName
      : typeof method?.key === "string"
        ? method.key
        : "";
  return {
    flow: "approve",
    fromWidthMm: sent.widthMm,
    fromHeightMm: sent.heightMm,
    widthMm: raw.widthMm,
    heightMm: raw.heightMm,
    maxSize,
    method: methodName,
  };
}

/**
 * The ONE success text of a flow in which the server scaled the logo down:
 * the flow's own success text followed by what happened to the size. `t` is
 * passed in (the action handlers have their own; tests pin the key).
 */
export function maxSizeClampSuccessText(
  successMessage: string,
  clamp: LogoMaxSizeClamp,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const detail = t(MAX_SIZE_CLAMP_TOAST_KEY, {
    fromWidth: clamp.fromWidthMm,
    fromHeight: clamp.fromHeightMm,
    width: clamp.widthMm,
    height: clamp.heightMm,
    method: clamp.method,
    maxSize: clamp.maxSize,
  });
  return successMessage ? `${successMessage}. ${detail}` : detail;
}
